import { faPen, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"
import { Loading, Modal, Pagination, TextArea, TextInput } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import DeleteConfirmationModal from "components/Shared/ConfirmationModal/DeleteConfirmationModal"
import { GET_ITEM_LIMIT } from "constants/constants"
import useCommon from "hooks/useCommon"
import { useState } from "react"
import BaseTable, { AutoResizer, ColumnShape } from "react-base-table"
import {
  AssetOutcomeRecord,
  useCreateAssetOutcomeMutation,
  useDeleteAssetOutcomeMutation,
  useListAssetOutcomesQuery,
  useUpdateAssetOutcomeMutation,
} from "services/aimsBackend"

export default function OutcomesTab() {
  const { showSnackBar } = useCommon()

  const { data: items = [], isFetching } = useListAssetOutcomesQuery()
  const [createOutcome, { isLoading: isCreating }] = useCreateAssetOutcomeMutation()
  const [updateOutcome, { isLoading: isUpdating }] = useUpdateAssetOutcomeMutation()
  const [deleteOutcome, { isLoading: isDeleting }] = useDeleteAssetOutcomeMutation()

  const [search, setSearch] = useState("")
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(GET_ITEM_LIMIT)

  const [showAddModal, setShowAddModal] = useState(false)
  const [addTitle, setAddTitle] = useState("")
  const [addError, setAddError] = useState("")

  const [editItem, setEditItem] = useState<AssetOutcomeRecord | null>(null)
  const [editTitle, setEditTitle] = useState("")
  const [editError, setEditError] = useState("")

  const [deleteTarget, setDeleteTarget] = useState<AssetOutcomeRecord | null>(null)

  const filteredRows = items.filter(item => item.title.toLowerCase().includes(search.toLowerCase()))
  const pagedRows = filteredRows.slice(
    pageNumber === 1 ? 0 : (pageNumber - 1) * pageSize,
    (pageNumber - 1) * pageSize + pageSize,
  )

  const formatApiError = (err: any, fallback: string) => {
    const data = err?.data
    if (typeof data === "string") return data
    if (data && typeof data === "object") {
      return Object.entries(data)
        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
        .join("; ")
    }
    return fallback
  }

  const handleAdd = async () => {
    if (!addTitle.trim()) {
      setAddError("Title is required")
      return
    }
    try {
      await createOutcome({ title: addTitle.trim() }).unwrap()
      showSnackBar("Outcome added successfully", "success")
      setAddTitle("")
      setAddError("")
      setShowAddModal(false)
    } catch (err) {
      showSnackBar(formatApiError(err, "Failed to create outcome"), "error")
    }
  }

  const handleEdit = async () => {
    if (!editTitle.trim()) {
      setEditError("Title is required")
      return
    }
    if (!editItem) return
    try {
      await updateOutcome({ id: editItem.id, body: { title: editTitle.trim() } }).unwrap()
      showSnackBar("Outcome updated successfully", "success")
      setEditItem(null)
      setEditTitle("")
      setEditError("")
    } catch (err) {
      showSnackBar(formatApiError(err, "Failed to update outcome"), "error")
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteOutcome(deleteTarget.id).unwrap()
      showSnackBar("Outcome deleted successfully", "success")
    } catch (err) {
      showSnackBar(formatApiError(err, "Failed to delete outcome"), "error")
    } finally {
      setDeleteTarget(null)
    }
  }

  const columns: Array<ColumnShape<AssetOutcomeRecord>> = [
    { key: "title", dataKey: "title", title: "Title", width: 300, flexGrow: 1 },
    {
      key: "action",
      title: "",
      width: 260,
      cellRenderer: ({ rowData }) => (
        <div className="d-flex">
          <Button
            action={() => {
              setEditItem(rowData)
              setEditTitle(rowData.title)
              setEditError("")
            }}
            label="Edit"
            icon={faPen}
            className="btn-full blue action-btn"
          />
          <Button
            action={() => setDeleteTarget(rowData)}
            label="Delete"
            icon={faTrash}
            className="btn-full danger action-btn ml-2"
          />
        </div>
      ),
    },
  ]

  return (
    <>
      <div className="mt-3">
        <div className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
          <Loading active={isFetching || isDeleting} />
          <div style={{ flex: 1, maxWidth: 400 }}>
            <TextInput
              labelText="Search"
              hideLabel
              value={search}
              onChange={e => {
                setSearch(e.target.value)
                setPageNumber(1)
              }}
              placeholder="Search outcomes..."
            />
          </div>
          <Button
            action={() => {
              setAddTitle("")
              setAddError("")
              setShowAddModal(true)
            }}
            label="Add outcome"
            className="btn-full green"
            icon={faPlus}
          />
        </div>

        <div style={{ height: "100%", minHeight: "60vh", marginTop: 10 }}>
          <AutoResizer>
            {({ width, height }) => (
              <BaseTable
                data={pagedRows}
                width={width}
                height={height}
                rowKey="id"
                className="custom-table"
                rowHeight={38}
                headerHeight={45}
                columns={columns}
                emptyRenderer={() => <p>No records to display</p>}
              />
            )}
          </AutoResizer>
        </div>

        <Pagination
          totalItems={filteredRows.length}
          pageSize={pageSize}
          pageSizes={[10, 20, 30]}
          page={pageNumber}
          onChange={(event: any): void => {
            setPageNumber((event as any).page)
            setPageSize((event as any).pageSize)
          }}
        />
      </div>

      <Modal
        open={showAddModal}
        modalHeading="Add Outcome"
        primaryButtonText={isCreating ? "Adding..." : "Add"}
        secondaryButtonText="Cancel"
        onRequestClose={() => setShowAddModal(false)}
        onSecondarySubmit={() => setShowAddModal(false)}
        onRequestSubmit={handleAdd}
        primaryButtonDisabled={isCreating}
      >
        <TextArea
          labelText="Title"
          value={addTitle}
          onChange={e => {
            setAddTitle(e.target.value)
            if (addError) setAddError("")
          }}
          placeholder="Enter outcome title"
          invalid={!!addError}
          invalidText={addError}
          rows={4}
        />
      </Modal>

      <Modal
        open={!!editItem}
        modalHeading="Edit Outcome"
        primaryButtonText={isUpdating ? "Saving..." : "Save"}
        secondaryButtonText="Cancel"
        onRequestClose={() => setEditItem(null)}
        onSecondarySubmit={() => setEditItem(null)}
        onRequestSubmit={handleEdit}
        primaryButtonDisabled={isUpdating}
      >
        <TextArea
          labelText="Title"
          value={editTitle}
          onChange={e => {
            setEditTitle(e.target.value)
            if (editError) setEditError("")
          }}
          placeholder="Enter outcome title"
          invalid={!!editError}
          invalidText={editError}
          rows={4}
        />
      </Modal>

      <DeleteConfirmationModal
        open={!!deleteTarget}
        onRequestClose={() => setDeleteTarget(null)}
        onRequestSubmit={handleDelete}
      >
        Are you sure you want to delete &quot;{deleteTarget?.title}&quot;?
      </DeleteConfirmationModal>
    </>
  )
}
